// ✅ src/pages/VehicleV2/JobCardEntryDetailsReport_AllPrint.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/jobCardEntryReport";
const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

export default function JobCardEntryDetailsReport_Print() {
  const navigate = useNavigate();
  const { search } = useLocation();

  // ✅ supports:
  // 1) /page?123
  // 2) /page?id=123
  const jobcardEntryId = useMemo(() => {
    const s = (search || "").replace("?", "").trim();
    if (!s) return 0;

    // if it looks like "id=123"
    if (s.includes("=")) {
      const qs = new URLSearchParams(search);
      return toInt(qs.get("id"));
    }

    // else it's like "?123"
    return toInt(s);
  }, [search]);

  const [loading, setLoading] = useState(false);
  const [row, setRow] = useState(null);

  const mapRow = (r) => ({
    vehicleMainType: r.VehicleType ?? "",
    jobCardNo: r.fld_jobcardno ?? "",
    entryDate: r.fld_entrydate1 ?? "",
    vehicleName: r.fld_vehicle_name ?? "",
    vehicleRegNo: r.fld_vehicle_regno ?? "",
    location: r.fld_worklocationname?? "",

    hrKm:
      r.fld_hrkm ??
      r.fld_workinghrs ??
      r.fld_workingkm ??
      r.fld_hrs ??
      "",

    status: r.fld_status ?? "",
    complaint: r.fld_natureofcomplaint ?? "",
    attendedBy: r.fld_complaintattendedby ?? "",
    inspectedBy: r.fld_inspectedby ?? "",
    remedy: r.fld_remedy ?? "",

    fromDate: r.fld_maintainfromdate1?? "",
    inTime: r.fld_maintainintime ?? "",
    toDate: r.fld_maintaintodate1 ?? "",
    outTime: r.fld_maintainouttime ?? "",

    hrs: r.fld_hours ?? r.fld_maintainhrs ?? r.fld_hrs ?? "",

    sparesDetails: r.fld_sparedetails ?? "",
    maintenanceBy: r.fld_maintenanceby ?? "",
  });

  const fetchOne = async () => {
    if (!jobcardEntryId) return;

    try {
      setLoading(true);

      // ✅ This endpoint should return ONE row
      // { success:true, row:{...} }
      const res = await axios.get(`${API_BASE}/print/${jobcardEntryId}`);

      if (!res.data?.success) throw new Error(res.data?.message || "Failed");
      const r = res.data.row;
      setRow(mapRow(r));
    } catch (e) {
      setRow(null);
      alert(e?.response?.data?.message || e.message || "Failed to load print data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOne();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobcardEntryId]);

  const handlePrint = () => window.print();

  return (
    <div className="min-h-screen bg-white p-4">
      {/* Buttons hidden in print */}
      <div className="print:hidden mb-6 text-center flex gap-3 justify-center">
        <button
          onClick={() => navigate(-1)}
          className="bg-gray-200 text-black px-6 py-3 text-sm font-medium hover:bg-gray-300"
        >
          Back
        </button>
        <button
          onClick={handlePrint}
          className="bg-black text-white px-6 py-3 text-sm font-medium hover:bg-gray-800 transition-colors"
          disabled={loading}
        >
          {loading ? "Loading..." : "Print"}
        </button>
      </div>

      <div className="max-w-7xl mx-auto border-2 border-black p-3 print:max-w-none print:mx-0 print:border-2">
        <div className="border border-black p-3">
          {/* Header (match screenshot layout) */}
          <div className="border border-black p-3 mb-3 flex items-center gap-5">
            <div className="w-28 h-20 bg-black text-white flex flex-col justify-center items-center text-xs font-bold">
              <div>NISSI</div>
              <div>INFOTECH</div>
              <div className="text-xs">PVT LTD</div>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold mb-1">Nissi Infotech</h1>
              <div className="text-[11px] leading-tight">
                <div>Nissi</div>
                <div className="font-semibold">CIVIL CONSTRUCTION</div>
                <div>3/42-12, Nagappa colony , kavundampalayam ,Tamil Nadu, India .</div>
                <div className="mt-1">
                  Phone No :9003744644 &nbsp;&nbsp;&nbsp;&nbsp; infraconsoft-implementation@nissiinfotech.in
                </div>
              </div>
            </div>
          </div>

          <div className="text-center text-base font-bold my-3">
            Job Card Entry Detail Report
          </div>

          {/* Table (single row like screenshot) */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-black text-[10px]">
              <thead>
                <tr>
                  <th className="border border-black p-1 text-center font-bold">S. No</th>
                  <th className="border border-black p-1 text-center font-bold">Vehicle Main Type</th>
                  <th className="border border-black p-1 text-center font-bold">Job Card No</th>
                  <th className="border border-black p-1 text-center font-bold">Entry Date</th>
                  <th className="border border-black p-1 text-center font-bold">Vehicle Name</th>
                  <th className="border border-black p-1 text-center font-bold">Vehicle Reg No</th>
                  <th className="border border-black p-1 text-center font-bold">Location</th>
                  <th className="border border-black p-1 text-center font-bold">Hr/Km</th>
                  <th className="border border-black p-1 text-center font-bold">Status</th>
                  <th className="border border-black p-1 text-center font-bold">Nature Of Complaint</th>
                  <th className="border border-black p-1 text-center font-bold">Complaint Attended By</th>
                  <th className="border border-black p-1 text-center font-bold">Inspected By</th>
                  <th className="border border-black p-1 text-center font-bold">Remedy</th>
                  <th className="border border-black p-1 text-center font-bold">From Date</th>
                  <th className="border border-black p-1 text-center font-bold">In Time</th>
                  <th className="border border-black p-1 text-center font-bold">To Date</th>
                  <th className="border border-black p-1 text-center font-bold">Out Time</th>
                  <th className="border border-black p-1 text-center font-bold">Hrs</th>
                  <th className="border border-black p-1 text-center font-bold">Spares Details</th>
                  <th className="border border-black p-1 text-center font-bold">Maintenance By</th>
                </tr>
              </thead>

              <tbody>
                {row ? (
                  <tr>
                    <td className="border border-black p-1 text-center">1</td>
                    <td className="border border-black p-1 text-center">{row.vehicleMainType}</td>
                    <td className="border border-black p-1 text-center">{row.jobCardNo}</td>
                    <td className="border border-black p-1 text-center">{row.entryDate}</td>
                    <td className="border border-black p-1 text-center">{row.vehicleName}</td>
                    <td className="border border-black p-1 text-center">{row.vehicleRegNo}</td>
                    <td className="border border-black p-1 text-center">{row.location}</td>
                    <td className="border border-black p-1 text-center">{row.hrKm}</td>
                    <td className="border border-black p-1 text-center">{row.status}</td>
                    <td className="border border-black p-1 text-center">{row.complaint}</td>
                    <td className="border border-black p-1 text-center">{row.attendedBy}</td>
                    <td className="border border-black p-1 text-center">{row.inspectedBy}</td>
                    <td className="border border-black p-1 text-center">{row.remedy}</td>
                    <td className="border border-black p-1 text-center">{row.fromDate}</td>
                    <td className="border border-black p-1 text-center">{row.inTime}</td>
                    <td className="border border-black p-1 text-center">{row.toDate}</td>
                    <td className="border border-black p-1 text-center">{row.outTime}</td>
                    <td className="border border-black p-1 text-center">{row.hrs}</td>
                    <td className="border border-black p-1 text-center">{row.sparesDetails}</td>
                    <td className="border border-black p-1 text-center">{row.maintenanceBy}</td>
                  </tr>
                ) : (
                  <tr>
                    <td className="border border-black p-2 text-center" colSpan={20}>
                      {loading ? "Loading..." : "No record found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-3 p-2 border border-black bg-gray-100 text-[11px]">
            Report generated by ERP-Software - www.infraconsoft.com owned by www.nissiinfotech.com
          </div>
        </div>
      </div>

      <style jsx>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 8mm;
          }
          body {
            margin: 0;
            padding: 0;
          }
          .print\\:hidden {
            display: none !important;
          }
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          tr {
            page-break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
}
